import { useEffect, useState } from 'react';
import { useJobProfileStore } from '@/stores/jobProfileStore';
import { Button } from '@/components/ui/button';
import {
 Select,
 SelectContent,
 SelectItem,
 SelectTrigger,
 SelectValue,
} from '@/components/ui/select';
import { Terminal, Loader2, Plus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface InterviewSetupProps {
 onStart: (profileId: string, interviewType: string) => void;
 initialProfileId?: string;
}

export function InterviewSetup({
 onStart,
 initialProfileId,
}: InterviewSetupProps) {
 const navigate = useNavigate();
 const { profiles, fetchProfiles, isLoading } = useJobProfileStore();
 const [selectedId, setSelectedId] = useState<string>(initialProfileId || '');
 const [interviewType, setInterviewType] =
  useState<string>('technical_systems');

 useEffect(() => {
  fetchProfiles();
 }, [fetchProfiles]);

 useEffect(() => {
  if (initialProfileId) {
   setSelectedId(initialProfileId);
  }
 }, [initialProfileId]);

 const handleStart = () => {
  if (selectedId) {
   onStart(selectedId, interviewType);
  }
 };

 const hasProfiles = profiles.length > 0;
 const selectedProfile = profiles.find((p) => p.id === selectedId);

 if (isLoading && profiles.length === 0) {
  return (
   <div className='flex flex-col items-center justify-center h-full p-12 space-y-4'>
    <Loader2 className='w-8 h-8 text-primary animate-spin' />
    <p className='text-muted-foreground font-mono text-xs animate-pulse'>
     LOADING PROFILES...
    </p>
   </div>
  );
 }

 return (
  <div className='flex flex-col items-center justify-center space-y-8 p-12 text-center h-full max-w-lg mx-auto'>
   <div className='w-20 h-20 bg-primary/10 border border-primary/20 flex items-center justify-center rounded-none relative group'>
    <div className='absolute inset-0 bg-primary/20 blur opacity-0 group-hover:opacity-100 transition-opacity' />
    <Terminal className='w-10 h-10 text-primary relative z-10' />
   </div>

   <div className='space-y-4 w-full'>
    <h2 className='text-2xl font-bold font-mono text-primary uppercase tracking-tight'>
     Session Configuration
    </h2>
    <p className='text-muted-foreground font-mono text-xs max-w-sm mx-auto'>
     Select a target job profile to initialize the interview context logic and
     load relevant knowledge bases.
    </p>

    {!hasProfiles ? (
     <div className='bg-muted/10 border border-border/50 p-6 space-y-4'>
      <p className='text-sm text-foreground font-mono'>
       No job profiles detected.
      </p>
      <Button
       onClick={() => navigate('/profiles')}
       variant='outline'
       className='w-full rounded-none font-mono border-primary/50 hover:bg-primary/10'
      >
       <Plus className='w-4 h-4 mr-2' />
       CREATE PROFILE
      </Button>
     </div>
    ) : (
     <div className='space-y-6'>
      <div className='space-y-2 text-left'>
       <label className='text-xs font-mono text-muted-foreground uppercase'>
        Target Profile
       </label>
       <Select value={selectedId} onValueChange={setSelectedId}>
        <SelectTrigger className='w-full rounded-none bg-black/50 border-primary/20 font-mono'>
         <SelectValue placeholder='SELECT PROFILE...' />
        </SelectTrigger>
        <SelectContent className='rounded-none border-primary/20 bg-background/95 backdrop-blur-md'>
         {profiles.map((profile) => (
          <SelectItem
           key={profile.id}
           value={profile.id}
           className='font-mono text-xs focus:bg-primary/10 focus:text-primary rounded-none cursor-pointer'
          >
           {profile.jobTitle} @ {profile.companyName}
          </SelectItem>
         ))}
        </SelectContent>
       </Select>
       {selectedProfile && (
        <div className='text-[10px] bg-primary/5 border border-primary/10 p-2 font-mono text-muted-foreground'>
         SKILLS: {selectedProfile.skills.slice(0, 3).join(', ')}
         {selectedProfile.skills.length > 3 && '...'}
        </div>
       )}
      </div>

      <div className='space-y-2 text-left'>
       <label className='text-xs font-mono text-muted-foreground uppercase'>
        Interview Mode
       </label>
       <Select value={interviewType} onValueChange={setInterviewType}>
        <SelectTrigger className='w-full rounded-none bg-black/50 border-primary/20 font-mono'>
         <SelectValue />
        </SelectTrigger>
        <SelectContent className='rounded-none border-primary/20 bg-background/95 backdrop-blur-md'>
         <SelectItem value='technical_systems'>
          SYSTEMS DESIGN (Technical)
         </SelectItem>
         <SelectItem value='behavioral_culture'>
          BEHAVIORAL / CULTURE FIT
         </SelectItem>
         <SelectItem value='negotiation_salary'>SALARY NEGOTIATION</SelectItem>
         <SelectItem value='phone_screen'>INITIAL PHONE SCREEN</SelectItem>
        </SelectContent>
       </Select>
      </div>

      <Button
       onClick={handleStart}
       disabled={!selectedId}
       className='w-full rounded-none font-mono text-xs py-6 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50'
      >
       {selectedId ? 'INITIALIZE LINK' : 'SELECT PROFILE TO START'}
       <ArrowRight className='w-4 h-4 ml-2' />
      </Button>
     </div>
    )}
   </div>

   {/* Status Lines */}
   <div className='text-left w-full border-t border-border/20 pt-4 space-y-1 font-mono text-[10px] text-muted-foreground/50'>
    <p>&gt; SYSTEM: ONLINE</p>
    <p>&gt; AUDIO: STANDBY</p>
    <p>
     &gt; PROFILE STATUS:{' '}
     {selectedId ? (
      <span className='text-green-500'>LOADED</span>
     ) : (
      <span className='text-yellow-500'>WAITING</span>
     )}
    </p>
   </div>
  </div>
 );
}
